import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Icon, Back, OneMark } from "../shared/icons";
import { LANGUAGES } from "../shared/languages";
import {
  SLOT_LABEL, VERTICALS, FILES, DASHBOARDS, demoUrl, isPdf,
} from "../shared/data";
import useShareBasket from "../../hooks/useShareBasket";
import VideoFeed from "./VideoFeed";
import {
  Home, Vertical, Files, Dashboard, Capture, ReadyNow, Share, Inside, Assets,
  parseId, findVertical,
} from "./screens";
import "./camcom-next.css";

/* Lazy so `three` and the mascot kit land in their own chunk — /, /old and /new never fetch it,
   and /next only pays for it once Cammy is actually wanted. */
const Cammy = React.lazy(() => import("./Cammy"));

/*
 * Camcom Next — the expressive-dark sales kit at "/next".
 *
 * Same catalogue (../shared/data) and same share basket (../../hooks/useShareBasket)
 * as Camcom One at "/", so the two flows can never drift on content or collected state.
 * Only the presentation differs. The legacy option tree is still at "/old".
 */

const VKEYS = VERTICALS.map((v) => v.key);
/* screens that are a detail view OF something, so they owe you a way back */
const DETAIL = ["files", "dash", "capture"];
const TABS = [
  { key: "home", icon: Icon.Kit, label: "co_tab_kit" },
  { key: "demos", icon: Icon.Demo, label: "co_tab_ready" },
  { key: "assets", icon: Icon.Vid, label: "co_tab_assets" },
  { key: "share", icon: Icon.Share, label: "co_tab_share" },
  { key: "inside", icon: Icon.Company, label: "co_tab_company" },
];

/*
 * Whether to run the mascot at all.
 *
 * He is decoration, and decoration does not get to cost a demo. Skipped when the viewer asked
 * for reduced motion, when the device has no WebGL, and on short viewports — a 250px dock on a
 * phone in landscape covers the deliverable rows the rep is trying to show, and a WebGL loop
 * next to a 300MB streaming clip is battery nobody budgeted for.
 */
const cammyWanted = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return false;
  if (window.innerHeight < 620) return false;
  try {
    const probe = document.createElement("canvas");
    return !!(probe.getContext("webgl2") || probe.getContext("webgl"));
  } catch {
    return false;
  }
};

/*
 * Theme. Defaults to the viewer's OS preference, then remembers their choice.
 * Storage is guarded: it throws in private mode and when site data is blocked, and the
 * app must render either way — an unguarded read here would throw during render.
 */
const THEME_KEY = "camcom-next.theme";

const loadTheme = () => {
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch { /* blocked storage — fall through to the OS preference */ }
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
};

const Sun = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.6v2.2M12 19.2v2.2M4.2 12H2M22 12h-2.2M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" />
  </svg>
);
const Moon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 14.2A8.2 8.2 0 019.8 4a8.4 8.4 0 108.4 10.2h1.8z" />
  </svg>
);

/* Live demos open in a new tab. noopener/noreferrer so the target cannot reach back
   into this window via window.opener. */
const openDemo = (path) => {
  if (!path) return;
  window.open(demoUrl(path), "_blank", "noopener,noreferrer");
};

const CamcomNext = () => {
  const { t, i18n } = useTranslation();
  const basket = useShareBasket();
  const { picked, add, remove, clearAll, has, email, setEmail, note, setNote, sent, setSent } = basket;

  /* Presentation mode, permanently. The Present/Build toggle was removed from the bar; the
     kit is a customer-facing tool, so readiness chips and n/a rows stay hidden. The prop is
     still threaded through the screens, so restoring the toggle is a one-line change. */
  const present = true;
  const [theme, setTheme] = useState(loadTheme);
  const [screen, setScreen] = useState("home");
  const [ctxId, setCtxId] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [activeUC, setActiveUC] = useState({});
  const [sheet, setSheet] = useState(null);
  /* fid of the clip the Assets feed opened on, or null when the feed is closed */
  const [feedAt, setFeedAt] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  /* Evaluated once on mount rather than per render: the probe builds a canvas, and the answer
     cannot change for a given device mid-session. */
  const [showCammy] = useState(cammyWanted);
  const scrollRef = useRef(null);
  /* Scroll memory, per screen. Going back to a screen should land where you left it, not at
     the top — true for every screen, not just home. Written by go(), read by the restore effect
     below. */
  const scrollPos = useRef({});
  const viewKeyRef = useRef("home|");

  /* Remember the screen a detail view was opened FROM. Back used to be derived from ctxId
     alone, so opening a folder from "Ready now" sent you to the owning vertical — a screen you
     had never visited, with Ready now gone from the path entirely. */
  const go = useCallback((key, id) => {
    /* Snapshot where we are BEFORE anything unmounts. This used to be recorded from a scroll
       listener instead, which is corruptible: the wrapper empties while the screen swaps, the
       container collapses, the browser clamps scrollTop to fit and fires a scroll event — and
       that clamped value got written over the real one. Reading it here, at
       the instant of navigation, there is no window in which a clamp can be mistaken for the
       user's position. viewKeyRef rather than viewKey because this callback is stable. */
    if (scrollRef.current) scrollPos.current[viewKeyRef.current] = scrollRef.current.scrollTop;
    setScreen((prev) => {
      if (DETAIL.includes(key) && !DETAIL.includes(prev)) setOrigin(prev);
      else if (!DETAIL.includes(key)) setOrigin(null);
      return key;
    });
    if (id !== undefined) setCtxId(id);
    setSheet(null);
    setLangOpen(false);
    /* scroll position is restored per screen below, not reset here */
  }, []);

  /* One key per distinct view: a folder and a dashboard are different screens even though both
     are reached through `files`/`dash`, so ctxId is part of it. */
  const viewKey = `${screen}|${ctxId ?? ""}`;
  viewKeyRef.current = viewKey;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const target = scrollPos.current[viewKey] ?? 0;
    if (target === 0) { el.scrollTop = 0; return undefined; }

    /* The incoming screen mounts a frame or two after the swap and its images decode later
       still, so for the first frames the container is too short to scroll to `target` and the
       assignment silently clamps.

       This used to retry for a fixed 30 frames and then give up. 30 frames is ~500ms, and the
       screens that take longer than that are exactly the ones worth returning to: home, whose
       height depends on the hero plate decoding, and any vertical carrying a detection panel. On
       a cold cache or a mid-range phone the content landed after the budget expired and the user
       was dumped at the top — the "sometimes" in the bug report.

       So drive it off layout instead of off a frame count: re-apply whenever the content grows,
       and stop as soon as the scroll actually takes. The timeout is a backstop for a screen that
       genuinely never gets tall enough (content was removed since the visit), not the mechanism. */
    let done = false;
    let raf = 0;
    let timer = 0;
    let observer = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (observer) observer.disconnect();
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };

    const apply = () => {
      if (done) return;
      el.scrollTop = target;
      if (Math.abs(el.scrollTop - target) <= 1) finish();
    };

    /* scrollHeight only changes when the content box does, so watch the content, not the
       scroller — observing el itself reports its own fixed height and never fires. */
    if (el.firstElementChild && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(apply);
      observer.observe(el.firstElementChild);
    }

    raf = requestAnimationFrame(function tick() {
      apply();
      if (!done) raf = requestAnimationFrame(tick);
    });
    timer = window.setTimeout(finish, 3000);

    return finish;
  }, [viewKey]);



  useEffect(() => {
    try { window.localStorage.setItem(THEME_KEY, theme); }
    catch { /* blocked storage — the choice still holds for this session */ }
  }, [theme]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setSheet(null); setLangOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const subFor = useCallback((g) => `${t(g.v.name)} · ${t(g.u.name)}`, [t]);

  const addFile = useCallback((fid) => {
    const [base, i] = fid.split("#");
    const f = (FILES[base] || [])[+i];
    if (f) add(fid, f.t, subFor(parseId(base)));
  }, [add, subFor]);
  const addAll = useCallback((id) => {
    /* skip the entries with nothing behind them — two folders mix real files with placeholders */
    (FILES[id] || []).forEach((f, i) => {
      if (f.src) add(`${id}#${i}`, f.t, subFor(parseId(id)));
    });
  }, [add, subFor]);
  const addDash = useCallback((id) => add(id, SLOT_LABEL.dash, subFor(parseId(id))), [add, subFor]);

  /* Every playable clip, in the order the Assets tab lists them, so opening one and scrolling
     moves through the same sequence the user was just looking at. */
  const videoFeed = useMemo(() => {
    const out = [];
    VERTICALS.forEach((v) => v.ucs.forEach((u) => Object.keys(SLOT_LABEL).forEach((slot) => {
      const id = `${v.key}|${u.key}|${slot}`;
      (FILES[id] || []).forEach((f, i) => {
        if (!f.src || isPdf(f.src)) return;
        out.push({ fid: `${id}#${i}`, src: f.src, title: f.t, meta: f.m,
          source: `${t(v.name)} · ${t(u.name)}` });
      });
    })));
    return out;
  }, [t]);
  const addLink = useCallback((id) => add(id, SLOT_LABEL.demo, subFor(parseId(id))), [add, subFor]);

  const onAct = useCallback((id) => {
    const g = parseId(id);
    if (g.slot === "dash") {
      if (DASHBOARDS[`${g.v.key}|${g.u.key}`]) go("dash", id);
      else setSheet({ kind: "soon", id });
      return;
    }
    if (g.slot === "demo") {
      const demo = g.u.d.demo;
      /* two capture flows -> let the leader choose; one link -> just launch it */
      if (demo.capture) { go("capture", id); return; }
      if (demo.url) { openDemo(demo.url); return; }
      setSheet({ kind: "soon", id });
      return;
    }
    go("files", id);
  }, [go]);

  const activeTab = useMemo(
    () => (VKEYS.includes(screen) || DETAIL.includes(screen) ? "home" : screen),
    [screen]);

  const back = useMemo(() => {
    if (DETAIL.includes(screen) && ctxId) {
      /* back to where you actually came from; the owning vertical is only the fallback */
      if (origin === "demos") return { label: t("co_tab_ready"), target: "demos" };
      if (origin && VKEYS.includes(origin)) {
        const ov = findVertical(origin);
        if (ov) return { label: t(ov.name), target: origin };
      }
      const g = parseId(ctxId);
      return { label: t(g.v.name), target: g.v.key };
    }
    if (VKEYS.includes(screen)) return { label: t("co_back_all"), target: "home" };
    return null;
  }, [screen, ctxId, origin, t]);

  const currentLang =
    LANGUAGES.find((l) => l.code === (i18n.language || "en").split("-")[0]) || LANGUAGES[3];

  const body = () => {
    if (screen === "home") {
      /* The hero plate is a picture of a use case, so the panel offers it. Set the use case
         BEFORE navigating, or the vertical opens on its first tab and the plate the viewer just
         tapped is not the one they land on. */
      const pickScene = (vk, uk) => {
        setActiveUC((p) => ({ ...p, [vk]: uk }));
        go(vk);
      };
      return <Home present={present} onGo={go} onPickScene={pickScene} />;
    }
    if (screen === "demos") return <ReadyNow present={present} has={has} onAct={onAct} />;
    if (screen === "assets") {
      return <Assets has={has}
        onOpenFile={(fid) => setSheet({ kind: "file", id: fid })}
        onOpenVideo={(fid) => setFeedAt(fid)} />;
    }
    if (screen === "share")
      return (
        <Share picked={picked} onRemove={remove} onClear={clearAll}
          email={email} onEmail={setEmail} note={note} onNote={setNote}
          sent={sent} onSend={() => setSent(true)} />
      );
    if (screen === "inside") return <Inside />;
    if (screen === "files")
      return <Files id={ctxId} has={has} onAddAll={addAll}
        onOpenFile={(fid) => setSheet({ kind: "file", id: fid })} />;
    if (screen === "dash") return <Dashboard id={ctxId} has={has} onAddDash={addDash} />;
    if (screen === "capture")
      return <Capture id={ctxId} present={present} has={has}
        onLaunch={(i) => openDemo((parseId(ctxId).u.d.demo.capture || [])[i])}
        onAddLink={addLink} />;
    if (VKEYS.includes(screen)) {
      const v = findVertical(screen);
      return (
        <Vertical vk={screen} present={present} has={has} onAct={onAct}
          activeUC={activeUC[screen] || v.ucs[0].key}
          setActiveUC={(uk) => setActiveUC((p) => ({ ...p, [screen]: uk }))} />
      );
    }
    return null;
  };

  const sh = (() => {
    if (!sheet) return null;
    if (sheet.kind === "file") {
      const [base, i] = sheet.id.split("#");
      const g = parseId(base);
      const f = (FILES[base] || [])[+i];
      if (!f) return null;
      return {
        kicker: `${t(g.u.name)} · ${t(SLOT_LABEL[g.slot])}`,
        title: t(f.t),
        body: f.src ? (isPdf(f.src) ? (
          /* The embedded viewer is the desktop reading experience. Mobile Safari and Android
             Chrome will not scroll a PDF in an iframe — they render page one and stop — so
             below 640px the CSS hides it and the Open button is the only way in. */
          <>
            <iframe className="cn-pdf" src={`${f.src}#view=FitH`} title={t(f.t)} />
            <div className="cn-pdfsmall">
              <span className="cn-kicker">{t("co_sheet_doc")}</span>
              <span className="what">{t(f.t)}</span>
              <span className="who">{t(f.m)}</span>
            </div>
          </>
        ) : (
          /* real asset — streams via HTTP range requests, so large files start at once */
          <video className="cn-player" src={f.src} controls autoPlay
            preload="metadata" controlsList="nodownload" />
        )) : (
          <div className="cn-slot">
            <span className="cn-kicker">{g.slot === "vid" ? t("co_sheet_video") : t("co_sheet_doc")}</span>
            <span className="what">{t(f.t)}</span>
            <span className="who">{t(f.m)}</span>
          </div>
        ),
        foot: (
          <>
            {isPdf(f.src) && (
              <a className="cn-btn ghost" style={{ marginTop: 0 }} href={f.src}
                target="_blank" rel="noopener noreferrer">{t("co_open_doc")}</a>
            )}
            <button className="cn-btn" style={{ marginTop: 0 }} onClick={() => addFile(sheet.id)}>
              {has(sheet.id) ? `${t("co_added")} ✓` : <><Icon.Plus />{t("co_add_file")}</>}
            </button>
            <button className="cn-btn ghost" style={{ marginTop: 0 }} onClick={() => setSheet(null)}>
              {t("co_close")}
            </button>
          </>
        ),
      };
    }
    const g = parseId(sheet.id);
    return {
      kicker: `${g.v.brand} · ${t(g.u.name)}`,
      title: t(SLOT_LABEL[g.slot]),
      body: (
        <div className="cn-slot">
          <span className="cn-kicker">{t("co_sheet_notyet")}</span>
          <span className="what">{t(SLOT_LABEL[g.slot])}</span>
          <span className="who">{g.u.d[g.slot].n ? t(g.u.d[g.slot].n) : t("co_sheet_notyet_b")}</span>
        </div>
      ),
      foot: (
        <button className="cn-btn ghost" style={{ marginTop: 0 }} onClick={() => setSheet(null)}>
          {t("co_close")}
        </button>
      ),
    };
  })();

  return (
    <div className="cn-root" data-theme={theme}>
      {/* drifting mesh + grain, behind everything */}
      <div className="cn-mesh" aria-hidden="true"><i /><i /><i /></div>
      <div className="cn-veil" aria-hidden="true" />
      <div className="cn-grain" aria-hidden="true" />

      <header className="cn-bar">
        {/* Same lockup as /new — one product, two presentations. OneMark is shared from
            shared/icons; its inner classes are styled for this flow in camcom-next.css.

            The COMPLETE CamCom lockup, tagline included and nothing cropped — the file is
            aicamera_frontend's own camcom-logo.png, so both products carry one artwork.

            Two consequences, both deliberate:
              - It is sized for the TAGLINE, not for the mark. The tagline is 6.8% of the
                artwork's height, so a bar-sized 38px logo rendered it at 2.6px — present but
                unreadable. At 96px it reads. That is what makes this app bar ~112px tall.
              - The tagline is inked rgb(26,26,26) on transparency and would vanish on the dark
                theme, so dark gets a reversed variant in which ONLY the tagline glyphs are
                recoloured to the ink. Shapes, positions and the red mark are byte-identical
                between the two files — it is a knockout, not a redraw. Replace it if the brand
                team has an official reversed lockup. */}
        <button className="cn-brand" onClick={() => go("home")} aria-label={t("co_a11y_home")}>
          <img
            src={`${process.env.PUBLIC_URL}/camcom-next/camcom-logo${theme === "dark" ? "-reversed" : ""}.png`}
            alt="CamCom — Eye the Future"
          />
          <span className="cn-rule" />
          <OneMark />
        </button>

        <nav className="cn-nav" aria-label={t("co_a11y_sections")}>
          {VERTICALS.map((v) => (
            <button key={v.key} aria-current={screen === v.key} onClick={() => go(v.key)}>
              {t(v.name)}
            </button>
          ))}
          <button aria-current={screen === "demos"} onClick={() => go("demos")}>{t("co_tab_ready")}</button>
        </nav>

        <div className="cn-barR">
          {/* Endonyms vary hugely in width — "ENG" is 3 characters, "русский" is 80px — and the
              wide ones pushed the appearance labels off a 360px bar. Narrow screens get the
              language code instead; the endonym is still the accessible name, and the menu it
              opens lists every language in full. */}
          <button className="cn-pill" onClick={() => setLangOpen((v) => !v)}
            aria-expanded={langOpen} aria-label={currentLang.label} title={currentLang.label}>
            <span className="cn-langfull">{currentLang.label}</span>
            <span className="cn-langcode">{currentLang.code.toUpperCase()}</span>
          </button>
          {/* Two labelled options rather than one icon that toggles. An icon-only toggle is
              ambiguous — a sun could mean "you are in light mode" or "switch to light mode" —
              and the old aria-label was hardcoded English besides. Here both states are visible
              and aria-pressed says which one you are in. The labels used to be dropped below
              1100px, which put a phone — the main way this is used — right back to two bare
              icons; dropping Share from this bar freed the width to keep them. */}
          <div className="cn-themeseg" role="group" aria-label={t("co_theme")}>
            <button type="button" aria-pressed={theme === "light"}
              onClick={() => setTheme("light")} title={t("co_theme_light")}>
              <Sun /><span>{t("co_theme_light")}</span>
            </button>
            <button type="button" aria-pressed={theme === "dark"}
              onClick={() => setTheme("dark")} title={t("co_theme_dark")}>
              <Moon /><span>{t("co_theme_dark")}</span>
            </button>
          </div>
          {/* Share lived here too. It is in the bottom tabs, with the same count badge, so this
              was a second control for one destination — and the tabs are now shown at every
              width, so nothing is lost on desktop by taking it out. */}
        </div>
      </header>

      <div className="cn-scroll" ref={scrollRef}>
        <div className="cn-wrap">
          {back && (
            <button className="cn-back" onClick={() => go(back.target)}>
              <Back />{back.label}
            </button>
          )}
          {/* No exit animation, and so no AnimatePresence.
              This used to be <AnimatePresence mode="wait"> with exit={{opacity:0}}. mode="wait"
              holds the incoming screen until the outgoing one finishes exiting — and the exit
              did not reliably finish. Every screen's root runs a `stagger` variant over its
              children, and an exit triggered while that stagger was still running left
              AnimatePresence waiting forever: the old screen sat at opacity 0 and the new one
              never mounted. Navigation silently did nothing.

              MEASURED on the committed build, before any of today's work, so this is not a new
              regression: clicking a vertical card 6 times at increasing delays failed 4-5 times
              out of 6, and the only successes were the longest waits (>2s after the screen
              mounted). In a live demo a rep taps immediately, which is precisely the failing
              case.

              Keying the div still remounts it per screen, so the incoming fade still plays. The
              200ms fade-out is the whole cost, and a screen that reliably changes is worth more
              than one that sometimes dissolves. */}
          <motion.div key={screen + String(ctxId) + String(present)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}>
            {body()}
          </motion.div>
        </div>
      </div>

      <nav className="cn-tabs" aria-label={t("co_a11y_main")}>
        {TABS.map(({ key, icon: Ic, label }) => (
          <button key={key} className="cn-tab" aria-current={activeTab === key} onClick={() => go(key)}>
            <Ic /><span>{t(label)}</span>
            {key === "share" && <i className={`b${picked.length ? " on" : ""}`}>{picked.length}</i>}
          </button>
        ))}
      </nav>

      {langOpen && (
        <>
          <div className="cn-scrim on" onClick={() => setLangOpen(false)} />
          <div className="cn-langs" role="menu">
            {LANGUAGES.map((l) => (
              <button key={l.code} role="menuitem" aria-current={l.code === currentLang.code}
                onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}>
                {l.label}
              </button>
            ))}
          </div>
        </>
      )}

      {feedAt && (
        <VideoFeed
          items={videoFeed}
          startIndex={Math.max(0, videoFeed.findIndex((x) => x.fid === feedAt))}
          onClose={() => setFeedAt(null)}
          has={has}
          onAdd={addFile}
        />
      )}

      {/* No Suspense fallback: there is nothing sensible to show in a mascot's place, and a
          spinner in the corner would be worse than his arriving a beat late. He is suppressed
          while the video feed is up — a full-screen clip is the one thing on /next that must
          have the screen to itself. */}
      {showCammy && !feedAt && (
        <React.Suspense fallback={null}>
          {/* Resolve the use case the SAME way the Vertical screen does — it falls back to the
              first tab, so activeUC is undefined until the rep actually picks one. Passing the
              raw value meant Insurance never matched "insurance|motor" and Cammy simply never
              reacted on the screen he has the most to say about. */}
          <Cammy screen={screen}
            useCase={VKEYS.includes(screen)
              ? (activeUC[screen] || findVertical(screen).ucs[0].key)
              : undefined} />
        </React.Suspense>
      )}

      <div className={`cn-scrim${sheet ? " on" : ""}`} onClick={() => setSheet(null)} />
      <div className={`cn-sheet${sheet ? " on" : ""}`} role="dialog" aria-modal="true">
        <div className="cn-grip" />
        {sh && (
          <>
            <span className="cn-kicker">{sh.kicker}</span>
            <h3>{sh.title}</h3>
            {sh.body}
            <div className="cn-foot">{sh.foot}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default CamcomNext;
